import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Drawer, Row, Space, Spin, Steps, Table, Tooltip, Typography, message } from 'antd';
import { ClearOutlined, FileSearchOutlined } from '@ant-design/icons';
import GcPagina from '../components/Layout/GcPagina';
import { FctTarjeta, fmtFechaHora } from '../fct/react';
import { CampoFusionado, InformeFusion, TraspasoTabla, interpretarErrorDuplicados, leerInformeFusion } from '../api/clientesDuplicadosApi';
import { FichasEnfrentadas } from '../components/Clientes/duplicados/FichasEnfrentadas';
import { PasoCierre, PasoFusion, PasoSeleccion, PasoSuperviviente, PasoVerificacion } from '../components/Clientes/duplicados/PanelesPaso';
import { PasoTratamiento } from '../components/Clientes/duplicados/PasoTratamiento';
import { formatearValor, nombreTabla } from '../components/Clientes/duplicados/reglas';
import { Paso, useAsistenteDuplicados } from '../components/Clientes/duplicados/useAsistenteDuplicados';

const { Text } = Typography;

const TITULOS: Record<Paso, string> = {
  1: 'Selección',
  2: 'Verificación',
  3: 'Superviviente',
  4: 'Fusión de datos',
  5: 'Tratamiento',
  6: 'Cierre',
};

/**
 * Herramienta de eliminación de duplicados de cliente (GESCL-T07, tramo 5; ruta
 * `/clientes/duplicados`, recurso `Clientes.Altas.Gestionar`). Dos zonas fijas (§4.0): a la
 * izquierda las dos fichas enfrentadas (izquierda = se conserva, derecha = se elimina), a la
 * derecha el hilo del proceso con sus 6 pasos. «Intercambiar columnas» y «Recargar los dos»
 * están siempre a la vista; el segundo es la herramienta con la que se levanta un bloqueo
 * (D-GC-41).
 */
const ClientesDuplicadosPage: React.FC = () => {
  const a = useAsistenteDuplicados();
  const [recargando, setRecargando] = useState(false);
  /** Enlace al último informe: sobrevive a «Tratar otro duplicado» (§4.6). */
  const [ultimoInformeId, setUltimoInformeId] = useState<number | null>(null);
  const [informe, setInforme] = useState<InformeFusion | null>(null);
  const [informeAbierto, setInformeAbierto] = useState(false);
  const [cargandoInforme, setCargandoInforme] = useState(false);

  const recargar = useCallback(async () => {
    setRecargando(true);
    try {
      const antes = a.comparacion?.regla.resultado;
      const res = await a.recargar();
      if (res) {
        if (antes && antes !== res.regla.resultado) {
          message.info(res.regla.bloqueo ? 'Las fichas han cambiado: ahora hay un bloqueo.' : 'El bloqueo ha desaparecido: el proceso puede seguir.');
        } else {
          message.success('Fichas recargadas.');
        }
      }
    } finally {
      setRecargando(false);
    }
  }, [a]);

  const verInforme = useCallback(async (id: number) => {
    setInformeAbierto(true);
    setCargandoInforme(true);
    try {
      setInforme(await leerInformeFusion(id));
    } catch (e) {
      message.error(interpretarErrorDuplicados(e).mensaje);
      setInforme(null);
    } finally {
      setCargandoInforme(false);
    }
  }, []);

  const idFusionActual = a.ejecucion.estado === 'completada' ? a.ejecucion.idFusion : null;
  useEffect(() => { if (idFusionActual !== null) setUltimoInformeId(idFusionActual); }, [idFusionActual]);

  const supervivienteFijado = a.pasoMaximo >= 4 && !(a.comparacion?.regla.bloqueo ?? false);

  const panel = (() => {
    switch (a.paso) {
      case 1: return <PasoSeleccion a={a} />;
      case 2: return <PasoVerificacion a={a} />;
      case 3: return <PasoSuperviviente a={a} recargando={recargando} onRecargar={recargar} />;
      case 4: return <PasoFusion a={a} />;
      case 5: return <PasoTratamiento a={a} />;
      case 6: return <PasoCierre a={a} onVerInforme={() => { if (a.ejecucion.idFusion !== null) verInforme(a.ejecucion.idFusion); }} />;
      default: return null;
    }
  })();

  const estadoPaso = (n: Paso): 'finish' | 'process' | 'wait' => {
    if (n === a.paso) return a.paso === 6 ? 'finish' : 'process';
    return n < a.pasoMaximo || (n === a.pasoMaximo && a.paso > n) ? 'finish' : 'wait';
  };

  return (
    // Cabecera de contexto (R-06). Los mandos se han repartido donde se usan (Dev, 2026-09-24):
    // intercambiar y recargar, entre las dos columnas; empezar de nuevo y el último informe, en
    // la cabecera del proceso. «Volver a clientes» sale: la navegación entre las herramientas de
    // clientes se resolverá para las tres a la vez.
    <GcPagina seccion="Clientes" titulo="Clientes duplicados" contexto="Tratamiento">
      <Row gutter={16}>
        <Col xs={24} lg={15}>
          <FctTarjeta>
            <Spin spinning={a.cargandoComparacion}>
              <FichasEnfrentadas
                comparacion={a.comparacion}
                paso={a.paso}
                supervivienteFijado={supervivienteFijado}
                edicion={a.edicion}
                catalogos={a.catalogos}
                acciones={a.paso === 4 ? {
                  editarValor: a.editarValor,
                  restaurarValor: a.restaurarValor,
                  cambiarElemento: a.cambiarElemento,
                  restaurarElemento: a.restaurarElemento,
                  marcarPrincipal: a.marcarPrincipal,
                  fijarPreferencias: a.fijarPreferencias,
                  aplicarReglas: a.aplicarReglasDatoGanador,
                } : undefined}
                eliminado={a.ejecucion.eliminacion !== null}
                controles={{
                  puedeIntercambiar: a.puedeIntercambiar,
                  intercambiada: a.intercambiada,
                  motivoNoIntercambiar: a.comparacion?.regla.forzado
                    ? 'La regla fija qué cliente se conserva: no se puede intercambiar'
                    : a.irreversibleIniciado ? 'El tratamiento ya se ha aplicado' : undefined,
                  onIntercambiar: a.intercambiar,
                  puedeRecargar: Boolean(a.comparacion) && !a.irreversibleIniciado,
                  recargando,
                  onRecargar: recargar,
                }}
              />
            </Spin>
          </FctTarjeta>
        </Col>
        <Col xs={24} lg={9}>
          <FctTarjeta>
            {/* Encabezado de la tarjeta con sus dos mandos a la derecha (FctTarjeta no tiene `extra`). */}
            <div className="fct-tarjeta__encabezado" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 className="fct-titulo-tarjeta">Proceso</h2>
              <Space size={2}>
                {ultimoInformeId !== null && a.paso !== 6 && (
                  <Tooltip title={`Ver el último informe (fusión nº ${ultimoInformeId})`}>
                    <Button type="text" size="small" icon={<FileSearchOutlined />} onClick={() => verInforme(ultimoInformeId)} aria-label="Ver el último informe" />
                  </Tooltip>
                )}
                <Tooltip title="Empezar de nuevo">
                  <Button type="text" size="small" icon={<ClearOutlined />} disabled={a.ejecucion.estado === 'en_curso'} onClick={a.reiniciar} aria-label="Empezar de nuevo" />
                </Tooltip>
              </Space>
            </div>
            {/* Títulos de paso, botones y chips a tamaño normal; lo explicativo (notas, resúmenes,
                ayudas) va en letra pequeña en cada panel (Dev, 2026-09-24). */}
            <Steps
              direction="vertical"
              size="small"
              current={a.paso - 1}
              onChange={(i) => a.irAPaso((i + 1) as Paso)}
              items={([1, 2, 3, 4, 5, 6] as Paso[]).map((n) => ({
                title: TITULOS[n],
                status: estadoPaso(n),
                disabled: !a.puedeIrAPaso(n),
                description: n === a.paso ? panel : undefined,
              }))}
            />
          </FctTarjeta>
        </Col>
      </Row>

      <Drawer
        title={informe ? `Informe de la fusión nº ${informe.idClienteFusion}` : 'Informe de la fusión'}
        open={informeAbierto}
        width={720}
        onClose={() => setInformeAbierto(false)}
      >
        {cargandoInforme && <Spin />}
        {informe && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <div>
              <Text strong className="fct-mono">{informe.codigoConservado}</Text> conserva · <Text strong className="fct-mono">{informe.codigoEliminado}</Text> {informe.nombreEliminado} eliminado
            </div>
            <Text type="secondary">
              Estado {informe.estado}{informe.conError ? ' (con error)' : ''} · regla: {informe.reglaSuperviviente ?? '—'} · por {informe.usuarioAlta}
              {informe.fechaAplicacion ? <> el <span className="fct-mono">{fmtFechaHora(informe.fechaAplicacion)}</span></> : ''}
            </Text>
            {informe.motivo && <Text>Motivo: {informe.motivo}</Text>}
            {informe.resultadoGfw && <Text>Cuenta web: {informe.resultadoGfw}</Text>}
            {informe.personaEliminada === false && <Text>La persona se conserva: {informe.personaConservadaPor}</Text>}
            <Table<CampoFusionado>
              size="small"
              pagination={false}
              rowKey={(k) => `${k.tabla}.${k.campo}`}
              title={() => 'Campos que cambiaron en el superviviente'}
              columns={[
                { title: 'Campo', dataIndex: 'campo', key: 'c' },
                { title: 'Antes', dataIndex: 'valorAntes', key: 'a', render: (v: string | null) => formatearValor(v) || '—' },
                { title: 'Del duplicado', dataIndex: 'valorDuplicado', key: 'd', render: (v: string | null) => formatearValor(v) || '—' },
                { title: 'Después', dataIndex: 'valorDespues', key: 'p', render: (v: string | null) => formatearValor(v) || '—' },
                { title: 'Origen', dataIndex: 'origen', key: 'o', width: 90 },
              ]}
              dataSource={informe.campos}
            />
            <Table<TraspasoTabla>
              size="small"
              pagination={false}
              rowKey={(t) => `${t.tabla}.${t.columna}`}
              title={() => 'Registros traspasados'}
              columns={[
                { title: 'Tabla', dataIndex: 'tabla', key: 't', render: (v: string) => `${nombreTabla(v)}${nombreTabla(v) !== v ? ` (${v})` : ''}` },
                { title: 'Columna', dataIndex: 'columna', key: 'c' },
                { title: 'Traspasadas', dataIndex: 'traspasadas', key: 'x', width: 110, align: 'right' as const, className: 'fct-num' },
                { title: 'Descartadas', dataIndex: 'descartadas', key: 'y', width: 110, align: 'right' as const, className: 'fct-num' },
              ]}
              dataSource={informe.traspasos.filter((t) => t.traspasadas > 0 || t.descartadas > 0)}
            />
          </Space>
        )}
      </Drawer>
    </GcPagina>
  );
};

export default ClientesDuplicadosPage;
